import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Stack,
  SegmentedControl,
  Textarea,
  TextInput,
  FileInput,
  Loader,
  Text,
  Box,
  Select,
  Group,
  Button,
} from '@mantine/core';
import { IconCamera, IconUpload } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { useMedplum } from '@medplum/react';
import { DiagnosticReport, Patient, Encounter } from '@medplum/fhirtypes';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { CodeableConceptInput } from '@/components/medplum-react/CodeableConceptInput/CodeableConceptInput';
import { DocumentScannerModal } from '@/components/DocumentScanner/DocumentScannerModal';
import { ARKON } from '@/consts/systems.consts';
import {
  PrenatalCheckFormState,
  PrenatalCheckResultValue,
} from '../utils/medical-history.types';
import {
  createInitialPrenatalCheckFormState,
  mapReportToFormState,
  validateFileForUpload,
  isValidDate,
  isFormValid,
  shouldShowReasonField,
  shouldShowTestTypeField,
  savePrenatalCheck,
  PRENATAL_CHECK_TEST_TYPE_OPTIONS,
  PRENATAL_CHECK_RESULT_OPTIONS,
  mapReportResultToPrenatalResult,
  mapPrenatalResultToReportResult,
} from '../utils/prenatal-check.utils';

interface PrenatalCheckModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  patient: Patient;
  encounter?: Encounter | null;
  report?: DiagnosticReport | null;
  readonly?: boolean;
}

export const PrenatalCheckModal: FC<PrenatalCheckModalProps> = ({
  opened,
  onClose,
  onSuccess,
  patient,
  encounter,
  report,
  readonly = false,
}) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages', 'common']);
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<PrenatalCheckFormState | null>(null);

  const [formState, setFormState] = useState<PrenatalCheckFormState>(
    createInitialPrenatalCheckFormState()
  );
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [scannerModalOpened, setScannerModalOpened] = useState(false);
  const [baselineSet, setBaselineSet] = useState(false);

  // Initialize form state
  useEffect(() => {
    if (opened) {
      const initialState = report
        ? mapReportToFormState(report)
        : createInitialPrenatalCheckFormState();
      setFormState(initialState);
      setBaselineSet(true);
    } else {
      baselineRef.current = null;
      setBaselineSet(false);
    }
  }, [opened, report]);

  // Capture baseline
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...formState };
      setBaselineSet(false);
    }
  }, [baselineSet, formState, opened]);

  // Validation
  const fileValidation = validateFileForUpload(formState.file);
  const hasValidDate = isValidDate(formState.date);
  const formIsValid = isFormValid(formState) && fileValidation.isValid;

  // Change detection
  const hasUnsavedChanges = useMemo(() => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(formState, baselineRef.current);
    } catch {
      return true;
    }
  }, [formState]);

  // Conditional field visibility
  const showReasonField = shouldShowReasonField(formState.checkType);
  const showTestTypeField = shouldShowTestTypeField(formState.checkType);

  const handleFileSelect = (file: File | null) => {
    setUploadingFile(true);
    setFormState((prev) => ({ ...prev, file }));
    setUploadingFile(false);
  };

  const handleScanComplete = (scannedFile: File) => {
    setFormState((prev) => ({ ...prev, file: scannedFile }));
    setScannerModalOpened(false);
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormState(baselineRef.current);
    } else {
      setFormState(createInitialPrenatalCheckFormState());
    }
  };

  const handleSave = async () => {
    if (!formIsValid || saving) {
      return;
    }

    setSaving(true);
    try {
      await savePrenatalCheck({
        medplum,
        patient,
        encounterId: encounter?.id,
        formState,
        existingReport: report || undefined,
      });

      showNotification({
        color: 'green',
        title: t('medicalHistory.prenatalCheck.saveSuccessTitle', {
          ns: 'patient-pages',
          defaultValue: 'Save successful',
        }),
        message: report
          ? t('medicalHistory.prenatalCheck.updateSuccessMessage', {
              ns: 'patient-pages',
              defaultValue: 'Prenatal check updated.',
            })
          : t('medicalHistory.prenatalCheck.createSuccessMessage', {
              ns: 'patient-pages',
              defaultValue: 'Prenatal check created.',
            }),
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save prenatal check:', error);
      showNotification({
        color: 'red',
        title: t('medicalHistory.prenatalCheck.saveErrorTitle', {
          ns: 'patient-pages',
          defaultValue: 'Save error',
        }),
        message:
          error instanceof Error
            ? error.message
            : t('medicalHistory.prenatalCheck.saveErrorMessage', {
                ns: 'patient-pages',
                defaultValue: 'An error occurred. Please try again.',
              }),
      });
    } finally {
      setSaving(false);
    }
  };

  const currentFileName = report?.presentedForm?.[0]?.title;

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={
        report
          ? t('medicalHistory.prenatalCheck.editTitle', {
              ns: 'patient-pages',
              defaultValue: 'Edit Prenatal Check',
            })
          : t('medicalHistory.prenatalCheck.createTitle', {
              ns: 'patient-pages',
              defaultValue: 'New Prenatal Check',
            })
      }
      size="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <CodeableConceptInput
          key={`checkType-${formState.checkType?.coding?.[0]?.code ?? 'new'}`}
          path="DiagnosticReport.code"
          binding={ARKON.VS.PREBIRTH_CHECKS_EL}
          label={t('medicalHistory.prenatalCheck.checkTypeLabel', {
            ns: 'patient-pages',
            defaultValue: 'Prenatal check type',
          })}
          name="checkType"
          maxValues={1}
          loadAll
          defaultValue={formState.checkType || undefined}
          onChange={(value) => {
            setFormState((prev) => ({
              ...prev,
              checkType: value || null,
              // Clear conditional fields when check type changes
              reasonForTest: '',
              testType: null,
            }));
          }}
          disabled={readonly}
        />

        {showReasonField && (
          <TextInput
            label={t('medicalHistory.prenatalCheck.reasonLabel', {
              ns: 'patient-pages',
              defaultValue: 'Reason for test',
            })}
            placeholder={t('medicalHistory.prenatalCheck.reasonPlaceholder', {
              ns: 'patient-pages',
              defaultValue: 'Describe the reason for the test',
            })}
            value={formState.reasonForTest}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                reasonForTest: e.currentTarget.value,
              }))
            }
            disabled={readonly}
          />
        )}

        {showTestTypeField && (
          <Select
            label={t('medicalHistory.prenatalCheck.testTypeLabel', {
              ns: 'patient-pages',
              defaultValue: 'Type of test performed',
            })}
            placeholder={t('medicalHistory.prenatalCheck.testTypePlaceholder', {
              ns: 'patient-pages',
              defaultValue: 'Select test type',
            })}
            data={PRENATAL_CHECK_TEST_TYPE_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={formState.testType}
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                testType: value as PrenatalCheckFormState['testType'],
              }))
            }
            clearable
            disabled={readonly}
          />
        )}

        <DateInputWrapper
          label={t('medicalHistory.prenatalCheck.dateLabel', {
            ns: 'patient-pages',
            defaultValue: 'Test date',
          })}
          value={formState.date ? new Date(formState.date) : null}
          onChange={(date) =>
            setFormState((prev) => ({
              ...prev,
              date: date ? date.toISOString().split('T')[0] : '',
            }))
          }
          maxDate={new Date()}
          required
          error={
            opened && !hasValidDate
              ? t('medicalHistory.prenatalCheck.invalidDate', {
                  ns: 'patient-pages',
                  defaultValue: 'Date cannot be in the future.',
                })
              : null
          }
          disabled={readonly}
        />

        <Box>
          <Text size="sm" fw={500} mb={4}>
            {t('medicalHistory.prenatalCheck.resultLabel', {
              ns: 'patient-pages',
              defaultValue: 'Result',
            })}
          </Text>
          <SegmentedControl
            fullWidth
            data={PRENATAL_CHECK_RESULT_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={
              mapReportResultToPrenatalResult(formState.result) || undefined
            }
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                result: mapPrenatalResultToReportResult(
                  value as PrenatalCheckResultValue | null
                ),
              }))
            }
            disabled={readonly}
          />
        </Box>

        <Textarea
          label={t('medicalHistory.prenatalCheck.observationsLabel', {
            ns: 'patient-pages',
            defaultValue: 'Observations',
          })}
          placeholder={t(
            'medicalHistory.prenatalCheck.observationsPlaceholder',
            {
              ns: 'patient-pages',
              defaultValue: 'Describe your observations',
            }
          )}
          value={formState.observations}
          onChange={(e) =>
            setFormState((prev) => ({
              ...prev,
              observations: e.currentTarget.value,
            }))
          }
          minRows={3}
          disabled={readonly}
        />

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t('medicalHistory.prenatalCheck.fileLabel', {
              ns: 'patient-pages',
              defaultValue: 'Test file (PDF)',
            })}
          </Text>

          <Group gap="sm" grow>
            <Button
              variant="light"
              leftSection={<IconCamera size={18} />}
              onClick={() => setScannerModalOpened(true)}
              disabled={uploadingFile || saving || readonly}
            >
              {t('medicalHistory.prenatalCheck.scanDocument', {
                ns: 'patient-pages',
                defaultValue: 'Scan document',
              })}
            </Button>

            <FileInput
              placeholder={
                currentFileName ||
                t('medicalHistory.prenatalCheck.filePlaceholder', {
                  ns: 'patient-pages',
                  defaultValue: 'Select file',
                })
              }
              accept="application/pdf"
              value={formState.file}
              onChange={handleFileSelect}
              disabled={uploadingFile || saving || readonly}
              rightSection={uploadingFile ? <Loader size="sm" /> : null}
              leftSection={<IconUpload size={18} />}
            />
          </Group>

          {formState.file &&
            (!fileValidation.isPdf || !fileValidation.isSizeValid) && (
              <Text size="xs" c="red">
                {!fileValidation.isPdf
                  ? t('medicalHistory.prenatalCheck.fileTypeError', {
                      ns: 'patient-pages',
                      defaultValue: 'File must be PDF.',
                    })
                  : t('medicalHistory.prenatalCheck.fileSizeError', {
                      ns: 'patient-pages',
                      defaultValue: 'File exceeds maximum size (10MB).',
                    })}
              </Text>
            )}

          {formState.file && (
            <Text size="xs" c="dimmed">
              {formState.file.name} (
              {(formState.file.size / 1024 / 1024).toFixed(2)} MB)
            </Text>
          )}
        </Stack>

        {!readonly && (
          <FormActionButtons
            ref={formActionsRef}
            onSave={handleSave}
            onDiscard={handleDiscardChanges}
            onClose={onClose}
            hasUnsavedChanges={hasUnsavedChanges}
            isLoading={saving || uploadingFile}
            isFormValid={formIsValid}
            saveLabel={t('common:save', { defaultValue: 'Save' })}
          />
        )}
      </Stack>

      <DocumentScannerModal
        opened={scannerModalOpened}
        onClose={() => setScannerModalOpened(false)}
        onScanComplete={handleScanComplete}
        preferredCamera="environment"
      />
    </Modal>
  );
};

export default PrenatalCheckModal;
